import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AR_BOARD_NAME, AR_MANDATE, AR_MEETING, AR_MEMBER, AR_METHODOLOGY, AR_ROLE, AR_RULING,
  AR_STATEMENT, AR_TOPIC, AR_VERDICT, arMap,
} from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import {
  ArrowLeft, ScrollText, Download, ShieldCheck, CalendarCheck, ChevronDown, Gavel,
  Send, CheckCircle2, XCircle, AlertTriangle, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  "Permissible": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  "Not permissible": { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
  "Restricted": { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle },
  "Required": { cls: "bg-sky-50 text-sky-700 border-sky-200", Icon: Info },
};

const Ruling = ({ r, ar, t }) => {
  const [open, setOpen] = useState(false);
  const v = VERDICT[r.verdict] || VERDICT.Restricted;
  const a = ar ? AR_RULING[r.slug] : null;
  return (
    <div className="rounded-2xl border border-border overflow-hidden bg-card" data-testid={`ruling-${r.slug}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-start p-4 sm:p-5 hover:bg-muted/40 transition-colors">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold shrink-0 ${v.cls}`}>
            <v.Icon className="h-3 w-3" />{ar ? arMap(AR_VERDICT, r.verdict) : r.verdict}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold leading-snug">{a?.question || r.question}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {ar ? arMap(AR_TOPIC, r.topic) : r.topic} · {t("bd_ruled")} {r.date}
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-border" data-testid={`ruling-body-${r.slug}`}>
          <p className="text-[15px] leading-[1.75] text-foreground/80">{a?.ruling || r.ruling}</p>
          {(a?.basis || r.basis) && (
            <p className="mt-3 text-xs text-muted-foreground">
              <span className="font-semibold">{t("bd_basis")}:</span> {a?.basis || r.basis}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default function ShariahBoard() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const [data, setData] = useState(null);
  const [rulings, setRulings] = useState([]);
  const [topic, setTopic] = useState("All");
  const [form, setForm] = useState({ name: "", email: "", topic: "", question: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shariah-board`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/shariah-board/rulings`).then((r) => setRulings(r.data.rulings)).catch(() => {});
  }, []);

  const shown = useMemo(
    () => (topic === "All" ? rulings : rulings.filter((r) => r.topic === topic)),
    [rulings, topic]
  );

  const ask = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API}/shariah-board/questions`, form);
      toast.success(res.message);
      setForm({ name: "", email: "", topic: "", question: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your question");
    } finally {
      setSending(false);
    }
  };

  const s = data?.settings;

  return (
    <div className="min-h-screen bg-background" data-testid="shariah-board-page">
      <PublicHeader />
      {/* hero */}
      <div className="navy-strip text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-sheen opacity-50" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">
          <div className="mb-8 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="board-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{t("hl_back")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <ShieldCheck className="h-3.5 w-3.5" />{t("bd_eyebrow")}
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="board-title">
            {t("bd_h1a")}<br />
            <span className="text-white/70">{t("bd_h1b")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">
            {ar ? AR_MANDATE : (s?.mandate || "The board sets the screening rules, reviews every product before launch and audits how we apply them.")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button className="rounded-full px-6"
              onClick={() => window.open(`${API}/shariah-board/certificate.pdf`, "_blank")}
              data-testid="board-certificate-download">
              <Download className="h-4 w-4 me-1.5" />{t("bd_download_cert")}
            </Button>
            <Button variant="outline" className="rounded-full px-6 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
              onClick={() => document.getElementById("ask-the-board")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="board-ask-scroll">
              {t("bd_ask_scroll")}
            </Button>
          </div>
          {s && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="board-facts">
              {[[t("bd_certificate"), s.certificate_id], [t("bd_last_audit"), s.last_audit],
                [t("bd_review_cycle"), s.review_cycle], [t("bd_next_review"), s.next_review]].map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">{k}</div>
                  <div className="mt-1 numeric text-sm font-semibold">{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20 space-y-16 sm:space-y-24">
        {/* members */}
        <section data-testid="board-members">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {ar ? AR_BOARD_NAME : (s?.board_name || t("bd_the_board"))}
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data?.members || []).map((m) => {
              const a = ar ? AR_MEMBER[m.name] : null;
              return (
              <div key={m.id} className="rounded-3xl border border-border bg-card p-6"
                data-testid={`board-member-${m.role.toLowerCase()}`}>
                <div className="h-12 w-12 rounded-2xl bg-[#1A1330] text-[color:var(--tharwy-lime)] flex items-center justify-center text-base font-bold tracking-tight">
                  {m.name.split(" ").slice(-2).map((x) => x[0]).join("")}
                </div>
                <h3 className="mt-4 text-base md:text-lg font-semibold leading-snug">{a?.name || m.name}</h3>
                <div className="mt-1 text-xs text-[#3E2E65] font-semibold">{ar ? arMap(AR_ROLE, m.role) : m.role}</div>
                <div className="text-xs text-muted-foreground">
                  {a?.credentials || m.credentials} · {a?.country || m.country} · {t("bd_since")} {m.since}
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a?.bio || m.bio}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(a?.expertise || m.expertise || []).map((e) => (
                    <span key={e} className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground">{e}</span>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </section>

        {/* methodology */}
        <section data-testid="board-methodology">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("bd_methodology_title")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("bd_methodology_sub")}</p>
          <div className="mt-8 space-y-3">
            {(data?.methodology || []).map((m) => {
              const a = ar ? AR_METHODOLOGY[m.stage] : null;
              return (
                <div key={m.stage} className="rounded-2xl border border-border bg-card p-5 sm:flex sm:gap-8"
                  data-testid={`methodology-${m.stage[0]}`}>
                  <div className="sm:w-56 shrink-0">
                    <div className="text-sm font-semibold">{a?.stage || m.stage}</div>
                    <div className="mt-1 text-xs text-[#3E2E65] font-semibold">{a?.test || m.test}</div>
                  </div>
                  <p className="mt-2 sm:mt-0 text-sm text-muted-foreground leading-relaxed">{a?.detail || m.detail}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* rulings */}
        <section data-testid="board-rulings">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("bd_rulings_title")}</h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                {t("bd_rulings_sub_a")} {shown.length} {t("bd_shown_of")} {rulings.length} {t("bd_shown")}.
              </p>
            </div>
            <Gavel className="h-8 w-8 text-[#3E2E65]/25 hidden sm:block" />
          </div>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="ruling-topics">
            {["All", ...(data?.topics || [])].map((tp) => (
              <button key={tp} onClick={() => setTopic(tp)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                  topic === tp ? "bg-[#3E2E65] text-white border-[#3E2E65]"
                               : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid={`ruling-topic-${tp.replace(/\s+/g, "-").toLowerCase()}`}>
                {ar ? arMap(AR_TOPIC, tp) : tp}
              </button>
            ))}
          </div>
          <div className="mt-5 space-y-2.5">
            {shown.map((r) => <Ruling key={r.id} r={r} ar={ar} t={t} />)}
          </div>
        </section>

        {/* meetings */}
        <section data-testid="board-meetings">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("bd_meetings")}</h2>
          <div className="mt-8 space-y-4">
            {(data?.meetings || []).map((m) => {
              const a = ar ? AR_MEETING[m.date] : null;
              return (
              <div key={m.id} className="relative ps-8 pb-2" data-testid={`meeting-${m.date}`}>
                <span className="absolute start-0 top-1.5 h-3 w-3 rounded-full bg-[#3E2E65] ring-4 ring-[#3E2E65]/15" />
                <span className="absolute start-[5px] top-6 bottom-0 w-px bg-border" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{a?.title || m.title}</span>
                  <span className="text-xs text-muted-foreground numeric">{m.date}</span>
                  {m.quorum && <span className="text-[10px] rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">{t("bd_quorum")} {m.quorum}</span>}
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a?.summary || m.summary}</p>
                <ul className="mt-3 space-y-1.5">
                  {(a?.decisions || m.decisions || []).map((d, i) => (
                    <li key={`${d}-${i}`} className="flex gap-2 text-xs text-foreground/75">
                      <CalendarCheck className="h-3.5 w-3.5 mt-0.5 text-[#3E2E65] shrink-0" />{d}
                    </li>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
        </section>

        {/* ask */}
        <section id="ask-the-board" className="rounded-3xl border border-border bg-card p-7 sm:p-10"
          data-testid="board-ask-section">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
            <ScrollText className="h-3.5 w-3.5" />{t("bd_ask_eyebrow")}
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">{t("bd_ask_title")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("bd_ask_sub")}</p>
          <form onSubmit={ask} className="mt-7 grid sm:grid-cols-2 gap-4 max-w-3xl">
            <Input required placeholder={t("bd_ask_name")} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ask-name" />
            <Input required type="email" placeholder={t("bd_ask_email")} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ask-email" />
            <Input placeholder={t("bd_ask_topic")} value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="sm:col-span-2" data-testid="ask-topic" />
            <Textarea required rows={5} placeholder={t("bd_ask_question")}
              value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
              className="sm:col-span-2" data-testid="ask-question" />
            <div className="sm:col-span-2">
              <Button type="submit" disabled={sending} className="rounded-full px-7" data-testid="ask-submit">
                <Send className="h-4 w-4 me-1.5" />{sending ? t("bd_ask_sending") : t("bd_ask_send")}
              </Button>
            </div>
          </form>
        </section>

        <p className="text-xs text-muted-foreground leading-relaxed">
          {ar ? AR_STATEMENT : s?.statement} {t("bd_footnote")}
        </p>
      </div>
    </div>
  );
}
